import { useCallback, useRef, useState } from 'react';
import ApiService from '@zola-helpers/client/dist/es/http/api';
import type { CamelCasedPropertiesDeep } from 'type-fest/index';
import type {
  CheckoutViewResponse,
  WCartView,
  WCheckoutCartView,
  WProductLookView,
  WProductView,
} from '@zola/svc-web-api-ts-client';
import { useEffectOnce } from '../../hooks/useEffectOnce';

type CheckoutResponse = CamelCasedPropertiesDeep<CheckoutViewResponse>;
type CheckoutCartViewDetails = CamelCasedPropertiesDeep<WCheckoutCartView>['details'];

export const useDGC = (): {
  addDGCToCart: (dgcSkuId: string) => Promise<WCartView | null>;
  digitalGreetingCardProduct: WProductView | undefined;
  digitalGreetingCards: WProductLookView[] | undefined;
  showDGCs: boolean;
} => {
  const isBusy = useRef(false);
  const [isBaby, setIsBaby] = useState<boolean>(false);
  const [digitalGreetingCardProduct, setDigitalGreetingCardProduct] = useState<WProductView>();
  const [cartDetails, setCartDetails] = useState<CheckoutCartViewDetails>();

  useEffectOnce(() => {
    ApiService.get<WProductView>('/web-registry-api/v1/greeting-card/product')
      .then((json) => {
        setDigitalGreetingCardProduct(json);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      });

    ApiService.get<CheckoutResponse>('/web-registry-api/v1/checkout')
      .then((json) => {
        const store = json.data?.cart?.store?.toString();
        setIsBaby(store === 'BABY_REGISTRY' || store === 'BABY_STORE');
        setCartDetails(json.data?.cart?.details);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      });
  });

  const addDGCToCart = useCallback(async (dgcSkuId: string) => {
    let result = null;

    if (isBusy.current) return result;

    isBusy.current = true;

    const body = {
      gift_message: ' ',
      item_origin: 'WEDDING_REGISTRY',
      quantity: 1,
      sku_id: dgcSkuId,
    };

    result = await ApiService.post<WCartView>(
      '/web-registry-api/v1/greeting-card/add-to-cart',
      body
    );

    isBusy.current = false;

    return result;
  }, []);

  const { product_look_views: digitalGreetingCards } = digitalGreetingCardProduct || {};
  const showDGCs = Boolean(cartDetails?.isRegistry && !cartDetails?.isOwnRegistry && !isBaby);

  return {
    addDGCToCart,
    digitalGreetingCardProduct,
    digitalGreetingCards,
    showDGCs,
  };
};
